import { Controller } from "@hotwired/stimulus"

/**
 * Planning Calendar Controller
 *
 * Handles calendar view interactions for the planning generator.
 * Supports month, week, and day views with navigation.
 * Manages the slide-over reassign/assign panel.
 */
export default class extends Controller {
  static targets = ["calendar", "title", "stats", "panel", "panelFrame"]

  static values = {
    view: { type: String, default: "week" },
    date: String,
    teamId: Number,
    staffMemberId: { type: Number, default: 0 },
    medicalRoleId: { type: Number, default: 0 },
    activityId: { type: Number, default: 0 }
  }

  connect() {
    this.selectedDate = this.dateValue ? new Date(this.dateValue) : new Date()
  }

  changeView(event) {
    const view = event.currentTarget.dataset.view
    this.viewValue = view
    this.updateViewButtons(event.currentTarget)
    this.refreshCalendar()
  }

  updateViewButtons(activeButton) {
    const buttons = this.element.querySelectorAll("[data-action*='changeView']")
    buttons.forEach(btn => {
      btn.classList.remove("active", "bg-[var(--pu-magenta)]", "text-white")
      btn.classList.add("bg-white")
    })
    activeButton.classList.add("active", "bg-[var(--pu-magenta)]", "text-white")
    activeButton.classList.remove("bg-white")
  }

  navigate(event) {
    const direction = parseInt(event.currentTarget.dataset.direction)
    this.navigateDate(direction)
    this.refreshCalendar()
  }

  navigateDate(direction) {
    if (this.viewValue === "day") {
      this.selectedDate.setDate(this.selectedDate.getDate() + direction)
    } else if (this.viewValue === "week") {
      this.selectedDate.setDate(this.selectedDate.getDate() + (direction * 7))
    } else {
      this.selectedDate.setMonth(this.selectedDate.getMonth() + direction)
    }
  }

  goToToday() {
    this.selectedDate = new Date()
    this.refreshCalendar()
  }

  filterByStaff(event) {
    this.staffMemberIdValue = parseInt(event.currentTarget.value) || 0
    this.refreshCalendar()
  }

  filterByRole(event) {
    this.medicalRoleIdValue = parseInt(event.currentTarget.value) || 0
    this.refreshCalendar()
  }

  filterByActivity(event) {
    this.activityIdValue = parseInt(event.currentTarget.value) || 0
    this.refreshCalendar()
  }

  refreshCalendar() {
    // Start from the live URL so existing params (notably generation_id when
    // viewing a draft) survive a view/date/filter change. Mirrors refreshGantt
    // in planning_gantt_controller.js; rebuilding from pathname dropped
    // generation_id and silently reverted to the published roster.
    const url = new URL(window.location.href)
    url.searchParams.set("view", this.viewValue)
    url.searchParams.set("date", this.formatDate(this.selectedDate))
    url.searchParams.set("team_id", this.teamIdValue)

    if (this.staffMemberIdValue > 0) {
      url.searchParams.set("staff_member_id", this.staffMemberIdValue)
    } else {
      url.searchParams.delete("staff_member_id")
    }

    if (this.medicalRoleIdValue > 0) {
      url.searchParams.set("medical_role_id", this.medicalRoleIdValue)
    } else {
      url.searchParams.delete("medical_role_id")
    }

    if (this.activityIdValue > 0) {
      url.searchParams.set("activity_id", this.activityIdValue)
    } else {
      url.searchParams.delete("activity_id")
    }

    Turbo.visit(url.toString(), { action: "replace" })
  }

  formatDate(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
  }

  // Slide-over panel for reassign/assign
  openPanel(event) {
    const panel = document.getElementById("calendar_panel_container")
    const backdrop = document.getElementById("calendar_panel_backdrop")
    if (panel) {
      panel.classList.remove("translate-x-full")
      panel.classList.add("translate-x-0")
    }
    if (backdrop) {
      backdrop.classList.remove("hidden")
    }
  }

  closePanel() {
    const panel = document.getElementById("calendar_panel_container")
    const backdrop = document.getElementById("calendar_panel_backdrop")
    if (panel) {
      panel.classList.remove("translate-x-0")
      panel.classList.add("translate-x-full")
    }
    if (backdrop) {
      backdrop.classList.add("hidden")
    }
  }
}
